import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from "vue";
import { BookingService } from "@/services/BookingService";
import { AccommodationsService } from "@/services/AccommodationsService";
import { useToast } from "@/services/useToast";
import Datepicker from "@vuepic/vue-datepicker";

const sortable = false;
const fixed = true;

export default /*#__PURE__*/_defineComponent({
  __name: 'AdminUpdateBooking',
  props: {
    bookingItems: {},
    bookingPublicReference: {}
  },
  emits: ["booking-updated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;
const allAccommodations = ref<any[]>([]);
const accommodationsLoading = ref(true);
const productsForSelectedAccommodation = ref<any[]>([]);
const accommodationService = new AccommodationsService();

const emptyNewItem = () => ({
  accommodation: "",
  city: "",
  address: "",
  product: "",
  startDate: "",
  endDate: "",
  numberOfPeople: 1,
  grossPricePerPersonPerNight: 0,
  retailPrice: 0,
});

const newItem = ref(emptyNewItem());
const isNewItemValid = computed(() => {
  const i = newItem.value;

  return (
    i.accommodation &&
    i.city &&
    i.address &&
    i.product &&
    i.startDate &&
    i.endDate &&
    i.numberOfPeople > 0 &&
    i.grossPricePerPersonPerNight > 0 &&
    i.retailPrice > 0 &&
    isDateRangeValid.value
  );
});

const isDateRangeValid = computed(() => {
  const i = newItem.value;
  if (!i.startDate || !i.endDate) return false;
  const start = new Date(i.startDate);
  const end = new Date(i.endDate);
  return end > start;
});

const addItemTooltip = computed(() => {
  const i = newItem.value;
  const missing: string[] = [];
  if (!i.accommodation) missing.push("Szálláshely");
  if (!i.city) missing.push("Város");
  if (!i.address) missing.push("Cím");
  if (!i.product) missing.push("Termék");
  if (!i.startDate) missing.push("Érkezés");
  if (!i.endDate) missing.push("Távozás");
  if (!i.startDate || !i.endDate || !isDateRangeValid.value)
    missing.push("A távozás az érkezés után kell legyen (min. 1 éj)");
  if (i.numberOfPeople <= 0) missing.push("Létszám (min. 1)");
  if (i.grossPricePerPersonPerNight <= 0) missing.push("Bekerülési ár");
  if (i.retailPrice <= 0) missing.push("Kiajánlott ár");
  return missing.length ? "Hiányzó: " + missing.join(", ") : "";
});

const fetchAllAccommodations = async () => {
  accommodationsLoading.value = true;
  try {
    const accommodations = await accommodationService.getAll();
    allAccommodations.value = accommodations;
  } catch (e) {
    console.log("error fetching accommodations", e);
  } finally {
    accommodationsLoading.value = false;
  }
};

const handleProductsComboboxChange = () => {
  const matchingProduct = productsForSelectedAccommodation.value.find(
    (product) => product.name == newItem.value.product
  );
  if (matchingProduct) {
    setItemStateFromProduct(matchingProduct);
  }
};

const setItemStateFromProduct = (product: any) => {
  newItem.value.grossPricePerPersonPerNight =
    product.grossPricePerPersonPerNight;
  newItem.value.retailPrice = product.grossPricePerPersonPerNight + 3000;
};

const handleAccommodationsComboboxChange = async () => {
  console.log("handleAccommodationsComboboxChange");
  clearLoadedProducts();
  resetNewItem();

  const matchingAccommodation = allAccommodations.value.find(
    (item) => item.name == newItem.value.accommodation
  );
  if (matchingAccommodation) {
    const fetchedAccommodationWithDetails =
      await accommodationService.getAccommodation(matchingAccommodation.id);
    productsForSelectedAccommodation.value =
      fetchedAccommodationWithDetails.products;
    newItem.value.city = fetchedAccommodationWithDetails.city ?? "";
    newItem.value.address = fetchedAccommodationWithDetails.address ?? "";
  }
};

function clearLoadedProducts() {
  productsForSelectedAccommodation.value = [];
}

function resetNewItem() {
  // Cleared up front so a free-text accommodation doesn't inherit the previous address.
  newItem.value.city = "";
  newItem.value.address = "";
  newItem.value.product = "";
  newItem.value.grossPricePerPersonPerNight = 0;
  newItem.value.retailPrice = 0;
}

const sanitizeRetailPrice = () => {
  const value = newItem.value.retailPrice;

  // Convert anything to string safely
  const raw = (value ?? "").toString();

  // Remove all non-digits
  const cleaned = raw.replace(/\D+/g, "");

  // Convert to number or null
  newItem.value.retailPrice = cleaned ? Number(cleaned) : 0;
};

const sanitizeGrossPrice = () => {
  const value = newItem.value.grossPricePerPersonPerNight;

  const raw = (value ?? "").toString();

  // Remove all non-digits
  const cleaned = raw.replace(/\D+/g, "");

  // Convert to number or null
  newItem.value.grossPricePerPersonPerNight = cleaned ? Number(cleaned) : 0;
};
const bookingsService = new BookingService("/admin/bookings");
const showCreateBookingDialog = ref(false);
const showBillWarningDialog = ref(false);
const { showToast } = useToast();

const openBookingDialogAndPrefillData = async () => {
  const hasBills = await bookingsService.hasBilledItems(
    props.bookingPublicReference
  );
  if (hasBills) {
    showBillWarningDialog.value = true;
  } else {
    openDialog();
  }
};

const confirmAndOpen = () => {
  showBillWarningDialog.value = false;
  openDialog();
};

const openDialog = () => {
  fetchAllAccommodations();
  indexedItems.value = [];
  props.bookingItems.forEach((item: any) => {
    indexedItems.value.push({
      ...item,
      _localId: generateLocalId(),
      index: indexedItems.value.length,
    });
  });
  showCreateBookingDialog.value = true;
};

const indexedItems = ref<any[]>([]);
const headers = [
  {
    title: "Szálláshely",
    sortable,
    fixed,
    align: "start",
    key: "accommodation",
  },
  { title: "Város", sortable, fixed, key: "city" },
  { title: "Termék", sortable, fixed, key: "product" },
  { title: "Érkezés", sortable, fixed, key: "startDate" },
  { title: "Távozás", sortable, fixed, key: "endDate" },
  {
    title: "Létszám",
    sortable,
    fixed,
    key: "numberOfPeople",
    width: "fit-content",
  },
  {
    title: "Bekerülési ár /fő/éj",
    sortable,
    fixed,
    key: "grossPricePerPersonPerNight",
  },
  { title: "Kiajánlott ár /fő/éj", sortable, fixed, key: "retailPrice" },
  { title: "Törlés", sortable, fixed, key: "index" },
];

const addItem = () => {
  indexedItems.value.push({
    ...newItem.value,
    _localId: generateLocalId(),
    index: indexedItems.value.length,
  });

  // reset form
  newItem.value = emptyNewItem();
};

const removeItem = (localId: string) => {
  console.log("removing item with localid: ", localId);
  indexedItems.value = indexedItems.value.filter((i) => i._localId !== localId);
};

const generateLocalId = () =>
  crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);

const updateBooking = async () => {
  try {
    const body = buildUpdateBookingBody();
    const bookingPublicReference = props.bookingPublicReference;
    const response = await bookingsService.update(body, bookingPublicReference);
    emit("booking-updated");
    showCreateBookingDialog.value = false;
    if (response.detachedBillCount > 0) {
      showToast(
        `Foglalás módosítva. Figyelem: ${response.detachedBillCount} számla leválasztásra került a régi foglalás elemeiről.`,
        "warning"
      );
    } else {
      showToast("Foglalás módosítva");
    }
  } catch {
    showToast("Foglalás módosítása sikertelen", "error");
  }
};

const buildUpdateBookingBody = () => {
  const body = indexedItems.value.map((item) => ({
    accommodation: item.accommodation,
    city: item.city,
    address: item.address,
    product: item.product,
    startDate: item.startDate,
    endDate: item.endDate,
    numberOfPeople: item.numberOfPeople,
    grossPricePerPersonPerNight: item.grossPricePerPersonPerNight,
    retailPrice: item.retailPrice,
    // Carried over so the versioned rebuild doesn't wipe IFA set in place on the details page.
    ifaAmount: item.ifaAmount,
    ifaCount: item.ifaCount,
  }));
  return body;
};

const closeBookingDialog = () => {
  console.log("closing booking dialog");
  showCreateBookingDialog.value = false;
};

const __returned__ = { props, emit, allAccommodations, accommodationsLoading, productsForSelectedAccommodation, accommodationService, emptyNewItem, newItem, isNewItemValid, isDateRangeValid, addItemTooltip, fetchAllAccommodations, handleProductsComboboxChange, setItemStateFromProduct, handleAccommodationsComboboxChange, clearLoadedProducts, resetNewItem, sanitizeRetailPrice, sanitizeGrossPrice, bookingsService, showCreateBookingDialog, showBillWarningDialog, showToast, openBookingDialogAndPrefillData, confirmAndOpen, openDialog, sortable, fixed, indexedItems, headers, addItem, removeItem, generateLocalId, updateBooking, buildUpdateBookingBody, closeBookingDialog, ref, computed, get BookingService() { return BookingService }, get AccommodationsService() { return AccommodationsService }, get useToast() { return useToast }, get Datepicker() { return Datepicker } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})